/**
 * TESTES DISC e QA — as chamadas da aba da ficha, de "Meus testes" e do link público.
 * Quem decide o que cada um vê é o SERVIDOR (`permissoesTestes`); a tela só mostra o que recebeu.
 */
import type { GerenciaQa } from './versao-qa';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, hasToken } from '../api';
import type { EstadoPublico, PainelDaFicha, RespostasDetalhadas, TesteNaFichaFuncionario, TipoTeste } from './tipos';

const BASE = '/api/v1/testes';
const chavePainel = (clienteId: string) => ['v1', 'testes', 'ficha', clienteId] as const;
const CHAVE_MEUS = ['v1', 'testes', 'meus'] as const;

/** 404 = esta pessoa não vê (ou a feature está desligada): a aba não aparece. */
async function ou404<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export function usePainelTestes(clienteId: string, ligado: boolean) {
  return useQuery({
    queryKey: chavePainel(clienteId),
    queryFn: () => ou404(api.get<PainelDaFicha>(`${BASE}/fichas/${encodeURIComponent(clienteId)}`)),
    enabled: hasToken() && ligado && !!clienteId,
  });
}

export function useMeusTestes(ligado: boolean) {
  return useQuery({
    queryKey: CHAVE_MEUS,
    queryFn: async () => (await ou404(api.get<{ testes: TesteNaFichaFuncionario[] }>(`${BASE}/meus`)))?.testes ?? null,
    enabled: hasToken() && ligado,
  });
}

export function buscarRespostas(resultadoId: string): Promise<RespostasDetalhadas> {
  return api.get<RespostasDetalhadas>(`${BASE}/resultados/${encodeURIComponent(resultadoId)}/respostas`);
}

/** `comoFuncionario`: o PDF de "Meus testes" — visão de funcionário ("seu/suas") também para a diretoria (A5). */
export function baixarPdfResultado(resultadoId: string, opts: { comoFuncionario?: boolean } = {}): Promise<void> {
  const qs = opts.comoFuncionario ? '?visao=funcionario' : '';
  return api.download(`${BASE}/resultados/${encodeURIComponent(resultadoId)}/pdf${qs}`);
}

export function baixarPdfRespostas(resultadoId: string): Promise<void> {
  return api.download(`${BASE}/resultados/${encodeURIComponent(resultadoId)}/respostas/pdf`);
}

export interface LinkCriado {
  linkId: string;
  caminho: string;
  expiraEmIso: string;
  dataValidade: string;
}

export function useTestesActions(clienteId: string) {
  const qc = useQueryClient();
  const recarregar = () => qc.invalidateQueries({ queryKey: chavePainel(clienteId) });
  const enviar = useMutation({
    // QA por gerência: `gerenciaQa` só quando a ficha não tem versão automática (o servidor ignora nos outros casos).
    mutationFn: (v: { tipo: TipoTeste; gerenciaQa?: GerenciaQa }) =>
      api.post<LinkCriado>(`${BASE}/fichas/${encodeURIComponent(clienteId)}/links`, v.gerenciaQa ? { tipo: v.tipo, gerenciaQa: v.gerenciaQa } : { tipo: v.tipo }),
    onSuccess: recarregar,
  });
  const cancelar = useMutation({
    mutationFn: (linkId: string) => api.post(`${BASE}/links/${encodeURIComponent(linkId)}/cancelar`, {}),
    onSuccess: recarregar,
  });
  const anular = useMutation({
    mutationFn: (v: { resultadoId: string; motivo: string }) =>
      api.post(`${BASE}/resultados/${encodeURIComponent(v.resultadoId)}/anular`, { motivo: v.motivo }),
    onSuccess: recarregar,
  });
  const desfazer = useMutation({
    mutationFn: (resultadoId: string) => api.post(`${BASE}/resultados/${encodeURIComponent(resultadoId)}/desfazer`, {}),
    onSuccess: recarregar,
  });
  const reenviar = useMutation({
    mutationFn: (tipo: TipoTeste) => api.post<LinkCriado>(`${BASE}/fichas/${encodeURIComponent(clienteId)}/reenviar`, { tipo }),
    onSuccess: recarregar,
  });
  const excluir = useMutation({
    mutationFn: () => api.delete(`${BASE}/fichas/${encodeURIComponent(clienteId)}`),
    onSuccess: recarregar,
  });
  return { enviar, cancelar, anular, desfazer, reenviar, excluir };
}

// ── O link público (sem login) ─────────────────────────────────────────────
const PUBLICO = '/api/v1/public/teste';
/** O link é de quem NÃO está logado: nunca anexa a sessão de quem por acaso abriu no mesmo navegador. */
const SEM_LOGIN = { skipAuth: true } as const;

/** O link público responde o estado mesmo nos erros conhecidos (409/429): a tela mostra a frase de cada um. */
async function publico(metodo: 'get' | 'post' | 'put', caminho: string, corpo?: unknown): Promise<EstadoPublico | { ok: true }> {
  try {
    if (metodo === 'get') return await api.get<EstadoPublico>(caminho, SEM_LOGIN);
    if (metodo === 'put') return await api.put<{ ok: true }>(caminho, corpo ?? {}, SEM_LOGIN);
    return await api.post<EstadoPublico>(caminho, corpo ?? {}, SEM_LOGIN);
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 429) return { estado: 'muitas_tentativas' };
      const estado = (e.response as { estado?: string } | undefined)?.estado;
      if (estado === 'vencido' || estado === 'encerrado') return { estado };
    }
    throw e;
  }
}

export const testePublico = {
  estado: (token: string) => publico('get', `${PUBLICO}/${encodeURIComponent(token)}`) as Promise<EstadoPublico>,
  aceitar: (token: string) => publico('post', `${PUBLICO}/${encodeURIComponent(token)}/aceite`) as Promise<EstadoPublico>,
  responder: (token: string, indice: number, valor: unknown) =>
    publico('put', `${PUBLICO}/${encodeURIComponent(token)}/respostas/${indice}`, { valor }),
  enviar: (token: string) => publico('post', `${PUBLICO}/${encodeURIComponent(token)}/enviar`) as Promise<EstadoPublico>,
  caminhoDoPdf: (token: string) => `${PUBLICO}/${encodeURIComponent(token)}/pdf`,
};
